import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { searchExpenseType } from "../api/expenseType";
import { showBackendMessage } from "../helpers/messagesHelper";
import Messages from "../localization/Messages";
import { ISearchParams, searchParamsInit } from "../models/searchParams";
import { IExpenseType } from "../models/expenseType";

/**
 * Custom hook for fetching expense types.
 * @param searchParams - The search parameters.
 * @returns An object containing the loading state, expense types, and total expense types.
 */
export function useFetchExpenseTypes(searchParams?: Partial<ISearchParams>) {
    const dispatch = useDispatch();
    const intl = useIntl();
    const [searchParamsString] = useState(() => JSON.stringify(searchParams));
    const [loading, setLoading] = useState(false);
    const [expenseTypes, setExpenseTypes] = useState<IExpenseType[]>([]);
    const [totalExpenseTypes, setTotalExpenseTypes] = useState(0);

    useEffect(() => {
        const fetchExpenseTypes = async () => {
            try {
                setLoading(true);
                const res = await searchExpenseType({
                    ...searchParamsInit,
                    ...JSON.parse(searchParamsString),
                });
                setExpenseTypes(res.data.rows ?? []);
                setTotalExpenseTypes(res.data.total);
            } catch {
                dispatch(showBackendMessage(intl, "error", "fetching", Messages.expenseTypes));
            } finally {
                setLoading(false);
            }
        };
        fetchExpenseTypes();
    }, [dispatch, intl, searchParamsString]);

    return { loading, expenseTypes, totalExpenseTypes } as const;
}
